import  { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";

import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import "chartjs-adapter-date-fns";
import moment from "jalali-moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  TimeScale
);

const GanttChart = ({ serverData, ranges, setRanges, diffInDays }) => {
  
  const currentGeorgianYear = moment().year();
  const [decades, setDecades] = useState([]);  
  const [firstDayOnAxisX, setFirstDayOnAxisX] = useState();
  const [lastDayOnAxisX, setLastDayOnAxisX] = useState();
  
  useEffect(() => {
    let sixDecades = [];
    let decade = [];
    
    if (serverData?.sowingDateRecommendation?.sowingDateRangeForecasts) {
      sixDecades =
        serverData?.sowingDateRecommendation?.sowingDateRangeForecasts?.reduce(
          (decades, item) => {
            decade.push(item);
            if (decade.length === 10) {
              decades.push(decade);
              decade = [];
            }
            return decades;
          },
          []
        );
      setDecades(sixDecades);
    }
  }, [serverData?.sowingDateRecommendation?.sowingDateRangeForecasts]);

  useEffect(() => {
    if (decades.length === 6) {
      setFirstDayOnAxisX(
        moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
          .add(decades[0][0]["sowingDayOfYear"], "days")
          .toDate()
      );
      setLastDayOnAxisX(
        moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
          .add(
            decades[5][9]["sowingDayOfYear"] +
              decades[5][9]["harvestDaysAfterSowingDayOfYear"],
            "days"
          )
          .toDate()
      );
    }
  }, [decades]);

  useEffect(() => {
    if (ranges.length === 0 && decades.length > 0) {
      decades.map((decade, dIdx) => {
        const sowingDayOfYearAverage = Math.round(
          decade?.reduce((total, item) => {
            return total + item.sowingDayOfYear;
          }, 0) / 10
        );
        const harvestDaysAfterSowingDayOfYearAverage = Math.round(
          decade?.reduce((total, item) => {
            return total + item.harvestDaysAfterSowingDayOfYear;
          }, 0) / 10
        );

        setRanges((prev) => [
          ...prev,
          {
            Start: moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(sowingDayOfYearAverage, "days")
              .toDate(),
            End: moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(
                sowingDayOfYearAverage + harvestDaysAfterSowingDayOfYearAverage,
                "days"
              )
              .toDate(),
            EventName: `${moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(sowingDayOfYearAverage, "days")
              .toDate()
              .toLocaleDateString("fa-IR")}  تا  ${moment(
              `${currentGeorgianYear}-01-01`,
              "YYYY-MM-DD"
            )
              .add(
                sowingDayOfYearAverage + harvestDaysAfterSowingDayOfYearAverage,
                "days"
              )
              .toDate()
              .toLocaleDateString("fa-IR")}`,
            EventSource: "",
          },
        ]);
      });
    }
  }, [decades]);

  Tooltip.positioners.custom = (elements, eventPosition) => {
    return {
      x: eventPosition.x,
      y: eventPosition.y,
    };
  };

  const labels = [...new Set(ranges.map((event) => event.EventSource))];

  const eventNames = [...new Set(ranges.map((event) => event.EventName))];

  const eventColors = [
    "rgba(255,99,132,0.5)", // قرمز
    "rgba(54,162,235,0.5)", // آبی
    "rgba(75,192,192,0.5)", // سبز
    "rgba(153,102,255,0.5)", // بنفش
    "rgba(255,159,64,0.5)", // نارنجی
    "rgba(255,99,71,0.5)", // گوجه‌ای
  ];

  const labelGrouping = [];

  const sortedData = ranges.sort(
    (a, b) => a.Start.getTime() - b.Start.getTime()
  );

  const datasets = sortedData.map((event) => {
    let start = event.Start.getTime();
    let end = event.End.getTime();

    let stack = undefined;
    let firstStackEntry = false;

    if (labelGrouping[event.EventSource] === undefined) {
      stack = { Stack: "Stack0", LastDate: event.End };
      labelGrouping[event.EventSource] = [stack];
      firstStackEntry = true;
    } else {
      labelGrouping[event.EventSource].forEach((item, index) => {
        if (
          stack === undefined &&
          item.LastDate.getTime() <= event.Start.getTime()
        ) {
          stack = { ...item };
          item.LastDate = event.End;
        }
      });
      if (stack === undefined) {
        const stackIndex = labelGrouping[event.EventSource].length;
        stack = { Stack: "Stack" + stackIndex, LastDate: event.End };
        labelGrouping[event.EventSource].push(stack);
        firstStackEntry = true;
      }
    }

    let data = labels.map(() => null);

    if (!firstStackEntry) {
      start -= stack.LastDate.getTime();
      end -= stack.LastDate.getTime();
    }
    data[labels.indexOf(event.EventSource)] = [
      start,
      end,
      format(event.Start, "HH:mm:ss.SSS") +
        " - " +
        format(event.End, "hh:mm:ss.SSS"),
    ];

    return {
      label: event.EventName,
      data: data,
      skipNull: true,
      backgroundColor: eventColors[eventNames.indexOf(event.EventName)],
      borderRadius: {
        topLeft: 4,
        topRight: 4,
        bottomLeft: 4,
        bottomRight: 4,
      }, // تنظیم شعاع گوشه‌های گرد
      // barThickness: 35, // تنظیم ضخامت نوار
      // categoryPercentage: 10, // تنظیم فاصله افقی نوارها
      barPercentage: 0.95, // تنظیم فاصله عمودی نوارها
      stack: event.EventSource + "_" + stack.Stack,
      datalabels: {
        formatter: () => event.EventName
        // formatter: () => {
        //   return `${diffInDays} روز` +  ' '  + event.EventName
        //  } ,
      },
    };
  });

  const data = {
    labels,
    datasets: datasets,
  };

  const options = {
    indexAxis: "y",
    plugins: {
      tooltip: {
        callbacks: {
          title: () => "",
          afterBody: (items) =>
            data.datasets[items[0].datasetIndex].data[items[0].dataIndex][2],
          label: (item) => data.datasets[item.datasetIndex].label,
        },
        position: "custom",
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "طول دوره رشد محصول شما با کاشت در تاریخ‌های مختلف",
        color: "black",
        font: {
          family: "B-Nazanin", // تنظیم فونت عنوان
          size: 15,
        },
        padding: {
          top: 0, // فاصله از بالای نمودار
          bottom: 10,
        },
      },
      datalabels: {
        display: true, // مطمئن شوید که datalabels فعال است
        color: "black",
        anchor: "center",
        align: "right",
        font: {
          size: 16,
          family: "B-Nazanin",
        },
      },
    },
    resizeDelay: 20,
    responsive: true,
    scales: {
      x: {
        min: moment(firstDayOnAxisX).toDate(),
        max: moment(lastDayOnAxisX).toDate(),
        type: "time",
        time: {
          displayFormats: {
            day: "jYYYY/jMM/jDD",
            week: "jYYYY/jMM/jDD",
            month: "jYYYY/jMM",
            year: "jYYYY",
          },
          unit: "month",
        },
        ticks: {
          callback: function (value) {
            return moment(value).toDate().toLocaleDateString("fa-IR");
          },
          color: "black",
          font: {
            family: "B-Nazanin",
            size: 15,
          },
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          font: {
            family: "B-Nazanin",
            weight: "bold",
            size: 14,
          },
        },
      },
    },
  };

  return <Bar options={options} data={data} />;
};

export default GanttChart;
